import { isGridInteractiveTarget } from '../utils/is-interactive-target'
import type {
  DataGrid,
  GridColumnInfo,
  GridContextMenuContext,
  GridContextMenuItem,
  GridContextMenuItemContext,
  GridContextMenuStore,
} from '../types'

export function createContextMenuStore(): GridContextMenuStore {
  return {
    visible: false,
    x: 0,
    y: 0,
    row: null,
    columnId: null,
  }
}

export function hasGridContextMenuConfig($grid: DataGrid) {
  const cfg = $grid.options.contextMenu
  if (!cfg || cfg.enabled === false) {
    return false
  }
  if (typeof cfg.items === 'function') {
    return true
  }
  return Array.isArray(cfg.items) && cfg.items.length > 0
}

export function canOpenGridContextMenu($grid: DataGrid, event: MouseEvent) {
  if (!hasGridContextMenuConfig($grid)) {
    return false
  }
  if (isGridInteractiveTarget(event.target)) {
    return false
  }
  return true
}

function resolveColumnFromEvent(
  $grid: DataGrid,
  event: MouseEvent
): GridColumnInfo | null {
  const el =
    event.target instanceof Element
      ? event.target
      : event.target instanceof Node
        ? event.target.parentElement
        : null
  const columnId = el?.closest('[data-column-id]')?.getAttribute('data-column-id')
  if (!columnId) {
    return null
  }
  return (
    $grid.internalData.allColumns.find((col) => col.id === columnId)
    ?? $grid.internalData.visibleColumns.find((col) => col.id === columnId)
    ?? null
  )
}

function resolveColumnFromStore($grid: DataGrid): GridColumnInfo | null {
  const columnId = $grid.reactData.contextMenuStore.columnId
  if (!columnId) {
    return null
  }
  return (
    $grid.internalData.allColumns.find((col) => col.id === columnId)
    ?? $grid.internalData.visibleColumns.find((col) => col.id === columnId)
    ?? null
  )
}

function isItemVisible(
  item: GridContextMenuItem,
  ctx: GridContextMenuContext,
  visibleMethod?: (ctx: GridContextMenuItemContext) => boolean
) {
  if (!visibleMethod) {
    return true
  }
  return visibleMethod({ ...ctx, item }) !== false
}

function stripNestedChildren(item: GridContextMenuItem): GridContextMenuItem {
  const { children: _ignored, ...rest } = item
  return rest
}

export function normalizeContextMenuItems(
  items: GridContextMenuItem[],
  ctx: GridContextMenuContext,
  visibleMethod?: (ctx: GridContextMenuItemContext) => boolean
): GridContextMenuItem[] {
  const result: GridContextMenuItem[] = []
  for (const item of items) {
    if (!isItemVisible(item, ctx, visibleMethod)) {
      continue
    }
    const children = (item.children ?? [])
      .map((child) => stripNestedChildren(child))
      .filter((child) => isItemVisible(child, ctx, visibleMethod))
    result.push(
      children.length ? { ...item, children } : { ...stripNestedChildren(item) }
    )
  }
  return result
}

export function resolveContextMenuItems(
  $grid: DataGrid,
  ctx?: Partial<GridContextMenuContext>
): GridContextMenuItem[] {
  const cfg = $grid.options.contextMenu
  if (!cfg || cfg.enabled === false) {
    return []
  }
  const row = ctx?.row ?? $grid.reactData.contextMenuStore.row
  if (!row) {
    return []
  }
  const column =
    ctx && 'column' in ctx ? (ctx.column ?? null) : resolveColumnFromStore($grid)
  const fullCtx: GridContextMenuContext = { row, column, $grid }
  const raw =
    typeof cfg.items === 'function' ? cfg.items(fullCtx) : (cfg.items ?? [])
  return normalizeContextMenuItems(raw, fullCtx, cfg.visibleMethod)
}

export function useContextMenu($grid: DataGrid) {
  const { reactData } = $grid

  $grid.closeContextMenu = () => {
    reactData.contextMenuStore.visible = false
    reactData.contextMenuStore.row = null
    reactData.contextMenuStore.columnId = null
  }

  $grid.openContextMenu = (event, row) => {
    if (!hasGridContextMenuConfig($grid)) {
      return false
    }
    const column = resolveColumnFromEvent($grid, event)
    const items = resolveContextMenuItems($grid, { row, column, $grid })
    if (!items.length) {
      return false
    }
    event.preventDefault()
    event.stopPropagation()
    $grid.setCurrentRow(row)
    Object.assign(reactData.contextMenuStore, {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      row,
      columnId: column?.id ?? null,
    })
    reactData.initStore.contextMenu = true
    return true
  }

  $grid.handleContextMenuCommand = (item) => {
    if (item.disabled || item.children?.length) {
      return
    }
    const row = reactData.contextMenuStore.row
    if (!row) {
      return
    }
    $grid.options.contextMenu?.onClick?.({
      code: item.code,
      item,
      row,
      column: resolveColumnFromStore($grid),
      $grid,
    })
    $grid.closeContextMenu?.()
  }
}
